import { useEffect, useMemo, useRef } from "react";
import { showInfoToast, showWarningToast, showErrorToast } from "../utils/toastUtils";

const parseThresholds = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return [70, 85, 95];
  const nums = raw
    .split(",")
    .map((v) => Number(String(v).trim()))
    .filter((n) => Number.isFinite(n) && n > 0 && n < 100);
  if (nums.length === 0) return [70, 85, 95];
  return [...new Set(nums)].sort((a, b) => a - b);
};

const clampPercent = (value) => Math.min(100, Math.max(0, Number(value) || 0));

const getStorageKey = (sessionId) =>
  sessionId ? `chat_session_usage_warned:${String(sessionId)}` : null;

const readShownThresholds = (sessionId) => {
  const key = getStorageKey(sessionId);
  if (!key) return new Set();
  try {
    const raw = sessionStorage.getItem(key);
    const arr = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(arr)) return new Set();
    return new Set(
      arr
        .map((n) => Number(n))
        .filter((n) => Number.isFinite(n)),
    );
  } catch {
    return new Set();
  }
};

const writeShownThresholds = (sessionId, set) => {
  const key = getStorageKey(sessionId);
  if (!key) return;
  try {
    sessionStorage.setItem(key, JSON.stringify([...set.values()]));
  } catch {}
};

export const useSessionUsageWarnings = ({
  sessionId,
  tokenUsage,
  sessionBlocked,
  thresholdsCsv = process.env.REACT_APP_SESSION_USAGE_WARNING_THRESHOLDS,
}) => {
  const thresholds = useMemo(
    () => parseThresholds(thresholdsCsv),
    [thresholdsCsv],
  );

  const lastSessionIdRef = useRef(null);
  const shownRef = useRef(new Set());

  useEffect(() => {
    if (lastSessionIdRef.current !== sessionId) {
      lastSessionIdRef.current = sessionId || null;
      shownRef.current = readShownThresholds(sessionId);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    if (!tokenUsage || typeof tokenUsage !== "object") return;
    if (sessionBlocked) return;

    const total = Number(tokenUsage.total_tokens ?? tokenUsage.totalTokens ?? 0) || 0;
    const limit = Number(tokenUsage.limit ?? 0) || 0;
    if (!(limit > 0)) return;

    const usedPercent = clampPercent((total / limit) * 100);
    const remainingPercent = Math.max(0, Math.round(100 - usedPercent));
    const remainingTokens = Math.max(0, Math.round(limit - total));

    const shown = shownRef.current;
    for (const t of thresholds) {
      if (usedPercent < t) continue;
      if (shown.has(t)) continue;

      const msgBase =
        remainingTokens > 0
          ? `${remainingPercent}% of your session remaining (${remainingTokens.toLocaleString()} tokens left).`
          : `${remainingPercent}% of your session remaining.`;

      if (t >= 95) {
        showErrorToast(`Session limit almost reached — only ${msgBase}`);
      } else if (t >= 85) {
        showWarningToast(`Warning: Only ${msgBase}`);
      } else {
        showInfoToast(`You have ${msgBase}`);
      }

      shown.add(t);
      writeShownThresholds(sessionId, shown);
    }
  }, [sessionBlocked, sessionId, thresholds, tokenUsage]);
};
